import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  useToast,
  Spinner,
  Button,
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { FaSync, FaPrint, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../Globalapi/Globalapi";

const TeacherHouse = () => {
  const toast = useToast();

  const [unassignedTeachers, setUnassignedTeachers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newAssignments, setNewAssignments] = useState([]);
  const userid = sessionStorage.getItem("user_id");

  // Fetch unassigned teachers
  const fetchUnassignedTeachers = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/house/display_unassigned_teachers.php`
      );
      if (response.data && response.data.success) {
        setUnassignedTeachers(response.data.data || []);
      } else {
        setUnassignedTeachers([]);
      }
    } catch (error) {
      setError("Error fetching teachers. Please try again later.");
    }
  };

  // Fetch houses and their assigned teachers
  const fetchHousesWithTeachers = async () => {
    try {
      const houseResponse = await axios.get(
        `${BASE_URL}/admin/house/list_all_house.php`
      );
      const assignedTeachersResponse = await axios.get(
        `${BASE_URL}/admin/house/list_assigned_teachers.php`
      );

      if (houseResponse.data && houseResponse.data.success) {
        const housesWithTeachers = houseResponse.data.events.map((house) => ({
          ...house,
          teachers: [],
        }));

        if (
          assignedTeachersResponse.data &&
          assignedTeachersResponse.data.success
        ) {
          assignedTeachersResponse.data.data.forEach((assignment) => {
            const houseIndex = housesWithTeachers.findIndex(
              (h) => h.id === assignment.house_id
            );
            if (houseIndex !== -1) {
              housesWithTeachers[houseIndex].teachers.push({
                teacher_id: assignment.teacher_id,
                staff_name: assignment.staff_name,
                image_url: assignment.thumb_img,
              });
            }
          });
        }

        setHouses(housesWithTeachers);
      } else {
        setError("Failed to fetch houses");
      }
    } catch (error) {
      setError(
        "Error fetching houses or assigned teachers. Please try again later."
      );
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchUnassignedTeachers(),
          fetchHousesWithTeachers(),
        ]);
      } catch (err) {
        setError("Error fetching data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userid]);

  // Handle drag end
  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const sourceHouseIndex = houses.findIndex(
      (house) => house.id === source.droppableId
    );
    const destinationHouseIndex = houses.findIndex(
      (house) => house.id === destination.droppableId
    );

    if (
      source.index === destination.index &&
      source.droppableId === destination.droppableId
    ) {
      return;
    }

    const movedTeacher =
      sourceHouseIndex !== -1
        ? houses[sourceHouseIndex].teachers[source.index]
        : unassignedTeachers[source.index];

    if (sourceHouseIndex !== -1) {
      houses[sourceHouseIndex].teachers.splice(source.index, 1);
    } else {
      setUnassignedTeachers((prev) =>
        prev.filter((_, idx) => idx !== source.index)
      );
    }

    if (destinationHouseIndex !== -1) {
      // Remove the teacher from the newAssignments if it's already there
      setNewAssignments((prev) =>
        prev.filter(
          (assignment) => assignment.teacher_id !== movedTeacher.teacher_id
        )
      );

      // Add the moved teacher to the destination house
      houses[destinationHouseIndex].teachers.push(movedTeacher);

      setNewAssignments((prev) => [
        ...prev,
        {
          house_id: houses[destinationHouseIndex].id,
          teacher_id: movedTeacher.teacher_id,
        },
      ]);
    }

    setHouses([...houses]);
  };

  // Function to automatically assign teachers to houses
  const autoAssignTeachers = () => {
    const housesCopy = [...houses];
    const teachersCopy = [...unassignedTeachers];

    housesCopy.forEach((house) => {
      if (teachersCopy.length > 0) {
        const teacher = teachersCopy.shift(); // Get the first teacher
        house.teachers.push({
          teacher_id: teacher.teacher_id,
          staff_name: teacher.staff_name,
          image_url: teacher.image_url,
        });
        // Store the new assignment
        setNewAssignments((prev) => [
          ...prev,
          { house_id: house.id, teacher_id: teacher.teacher_id },
        ]);
      }
    });

    setHouses(housesCopy);
    setUnassignedTeachers(teachersCopy); // Update the unassigned teachers list
  };

  // Save assigned teachers to the database
  const saveAssignments = async () => {
    if (newAssignments.length === 0) {
      toast({
        title: "No New Assignments",
        description: "There are no new assignments to save.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      pass: userid,
      house_teachers: newAssignments.map((assignment) => ({
        house_id: assignment.house_id,
        teacher_ids: [assignment.teacher_id],
      })),
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/admin/house/add_teachers_to_house.php`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast({
          title: "Assignments Saved Successfully",
          description: "The new assignments have been saved successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setNewAssignments([]); // Clear the new assignments list after successful save
        await fetchHousesWithTeachers(); // Refresh the UI after save
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error("API error:", error.response ? error.response.data : error);
      setError("Failed to save assignments. Please try again.");
    }
  };

  const handleDeleteTeacher = async (houseId, teacherId) => {
    // Find the house by ID
    const house = houses.find((h) => h.id === houseId);

    if (!house) {
      toast({
        title: "House Not Found",
        description: "The specified house could not be found.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Find the teacher within the house
    const teacher = house.teachers.find((t) => t.teacher_id === teacherId);

    if (!teacher) {
      toast({
        title: "Teacher Not Found",
        description: "The specified teacher could not be found in the house.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/house/delete_assigned_teachers.php`,
        {
          data: {
            teacher_id: teacher.teacher_id, // Pass the assigned ID in the payload
          },
        }
      );

      console.log("Delete Request for Teacher ID:", teacher.teacher_id);

      if (response.data.success) {
        toast({
          title: "Teacher Removed Successfully",
          description: "The teacher has been successfully removed.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Update the state after successful deletion
        const updatedHouses = houses.map((h) => {
          if (h.id === houseId) {
            return {
              ...h,
              teachers: h.teachers.filter((t) => t.teacher_id !== teacherId),
            };
          }
          return h;
        });

        setHouses(updatedHouses);
      } else {
        toast({
          title: "Failed to Remove Teacher",
          description:
            response.data.message ||
            "An error occurred while removing the teacher.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting teacher:", error);
      toast({
        title: "Error Removing Teacher",
        description:
          "An error occurred while removing the teacher. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box padding={5} bg="gray.50">
      <Flex direction="column" mb={4}>
        {/* Header with title and action buttons */}
        <Flex justify="space-between" align="center">
        <h1 style={{ fontSize: "20px", margin: 0 }}>Teacher House Assign</h1>

          <div className="action-buttons">
            <Button
              backgroundColor="white"
              fontSize="11px"
              boxShadow="md"
              marginRight={2}
              display={{ base: "none", md: "inline-flex" }} // Hide on mobile, show on larger screens
            >
              <FaPrint color="#515B73" />
              Print PDF
            </Button>
            <Button
              leftIcon={<FaSync />}
              colorScheme="blue"
              marginRight={2}
              onClick={autoAssignTeachers} // Attach the auto assignment function
            >
              <Box display={{ base: "none", md: "block" }}>Auto</Box>
            </Button>
            <Button
              colorScheme="teal"
              leftIcon={<FaPrint />}
              onClick={saveAssignments} // Attach the save function
            >
              <Box display={{ base: "none", md: "block" }}>Save</Box>
            </Button>
          </div>
        </Flex>

        {/* Breadcrumb right below the heading */}
        <div
          className="breadcrumb"
          style={{ marginBottom: "5px", marginTop: "5px" }}
        >
          <Link
            to="/admin"
            style={{ textDecoration: "none", color: "teal.600" }}
          >
            <span>Dashboard</span>
          </Link>
          /<span>House</span>/<span>Teacher House</span>
        </div>
      </Flex>

      {loading ? (
        <Spinner />
      ) : error ? (
        <Text color="red.500">{error}</Text>
      ) : (
        <Flex>
          <DragDropContext onDragEnd={handleDragEnd}>
            {/* Houses List */}
            <Flex direction={{ base: "column", md: "row" }} align="flex-start" >
              <DragDropContext onDragEnd={handleDragEnd}>
                {/* Unassigned Teachers List */}
                <Droppable droppableId="teachers">
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      // border="2px solid"
                      borderColor="teal.300"
                      borderRadius="xl"
                      bgGradient="linear(to-br, teal.100, white)"
                      padding="5"
                      width={{ base: "100%", md: "255px" }} // Full width on small screens
                      marginRight="20px"
                      boxShadow="lg"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        mb={4}
                        textAlign="center"
                        color="teal.600"
                      >
                        Teachers List
                      </Text>
                      {unassignedTeachers.length > 0 ? (
                        unassignedTeachers.map((teacher, index) => (
                          <Draggable
                            key={teacher.teacher_id}
                            draggableId={teacher.teacher_id}
                            index={index}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                // border="1px solid"
                                borderColor="teal.200"
                                borderRadius="lg"
                                padding="3"
                                marginBottom="3"
                                bg="teal.50"
                                _hover={{ bg: "teal.100" }}
                                transition="background 0.2s ease"
                              >
                                <Flex align="center">
                                  <Avatar
                                    name={teacher.staff_name}
                                    src={teacher.image_url}
                                    size="xs"
                                  />
                                  <Text
                                    marginLeft="12px"
                                    fontSize="xs"
                                    color="pink.700"
                                  >
                                    {teacher.staff_name}
                                  </Text>
                                </Flex>
                              </Box>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <Text color="gray.500">
                          No unassigned teachers available.
                        </Text>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>

                {/* Houses List */}
                <Flex flexWrap="wrap" justify="flex-start"  mt={{ base: 4, md: 0 }}>
                  {houses.map((house) => (
                    <Droppable droppableId={house.id} key={house.id}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          // border="2px solid"
                          borderColor="blue.300"
                          borderRadius="xl"
                          bgGradient="linear(to-br, blue.100, white)"
                          padding="5"
                          width={{ base: "100%", md: "220px" }} // Full width on small screens
                          marginRight="20px"
                          marginBottom="20px"
                          boxShadow="lg"
                        >
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            mb={3}
                            textAlign="center"
                            color="blue.600"
                          >
                            {house.caption}
                          </Text>
                          {house.teachers.length > 0 ? (
                            house.teachers.map((teacher, index) => (
                              <Draggable
                                key={teacher.teacher_id}
                                draggableId={teacher.teacher_id}
                                index={index}
                              >
                                {(provided) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    // border="1px solid"
                                    borderColor="blue.200"
                                    borderRadius="lg"
                                    padding="3"
                                    marginBottom="3"
                                    bg="blue.50"
                                    _hover={{ bg: "blue.100" }}
                                    transition="background 0.2s ease"
                                  >
                                    <Flex
                                      align="center"
                                      justify="space-between"
                                    >
                                      <Flex align="center">
                                        <Avatar
                                          name={teacher.staff_name}
                                          src={teacher.image_url}
                                          size="xs"
                                        />
                                        <Text
                                          fontSize="xs"
                                          marginLeft="12px"
                                          color="blue.700"
                                        >
                                          {teacher.staff_name}
                                        </Text>
                                      </Flex>
                                      <Button
                                        size="xs"
                                        bg="transparent"
                                        _hover={{ bg: "red.100" }}
                                        onClick={() =>
                                          handleDeleteTeacher(
                                            house.id,
                                            teacher.teacher_id
                                          )
                                        }
                                      >
                                        <FaTrash color="#d70707" size="14px" />
                                      </Button>
                                    </Flex>
                                  </Box>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <Text color="gray.500">
                              No teachers assigned to this house.
                            </Text>
                          )}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  ))}
                </Flex>
              </DragDropContext>
            </Flex>
          </DragDropContext>
        </Flex>
      )}
    </Box>
  );
};

export default TeacherHouse;
